import { createStore } from "zustand/vanilla";
import { immer } from "zustand/middleware/immer";
import { temporal } from "zundo";
import { applyBackgroundToAllScenes } from "@/features/lomdaEditorV2/lib/sceneUtils";
import {
    flattenChapterSceneIds,
    normalizeChapters,
} from "@/features/lomdaEditorV2/lib/chapterUtils";
import {
    directionOf,
    ensureSceneElements,
} from "@/features/lomdaEditorV2/lib/elements/migrateScene";
import { createDocumentSlice } from "./slices/documentSlice";
import { createUiSlice } from "./slices/uiSlice";
import { createAssetsSlice } from "./slices/assetsSlice";
import { canvasEqual, HISTORY_LIMIT, partializeCanvas } from "./canvasHistory";
import type { EditorGet, EditorSet, EditorState, EditorStoreInit } from "./types";

/** Order scenes to match the chapter source-of-truth (mirrors createInitialLomdaState). */
function seedScenes(init: EditorStoreInit) {
    const dir = directionOf(init.metadata.language);
    const withBg = applyBackgroundToAllScenes(init.content.scenes).map((s) =>
        ensureSceneElements(s, dir)
    );
    const chapters = normalizeChapters(withBg, init.content.chapters);
    const order = new Map<string, number>();
    flattenChapterSceneIds(chapters).forEach((id, i) => order.set(id, i));
    const scenes = [...withBg]
        .sort((a, b) => (order.get(a.id) ?? Infinity) - (order.get(b.id) ?? Infinity))
        .map((s, i) => (s.order === i ? s : { ...s, order: i }));
    return { scenes, chapters };
}

export function createEditorStore(init: EditorStoreInit) {
    const { scenes, chapters } = seedScenes(init);

    return createStore<EditorState>()(
        temporal(
            immer((rawSet, rawGet) => {
                const set = rawSet as unknown as EditorSet;
                const get = rawGet as unknown as EditorGet;
                return {
                    // identity (static)
                    userId: init.userId,
                    lomdaId: init.lomdaId,
                    mode: init.mode,
                    readOnly: init.readOnly,

                    ...createDocumentSlice(set, get),
                    ...createUiSlice(set, get),
                    ...createAssetsSlice(set, get),

                    // seed document + ui + assets from init
                    scenes,
                    chapters,
                    metadata: init.metadata,
                    lomdaName: init.lomdaName,
                    assets: init.assets,
                    selectedSceneId:
                        init.initialSelectedSceneId ?? scenes[0]?.id ?? null,
                };
            }),
            {
                limit: HISTORY_LIMIT,
                // History tracks only the canvas (per-scene elements / style /
                // layout) plus which scene is in view. Chapters, lomda name,
                // metadata, scene audio/script/order and interactions are
                // excluded — they are never recorded and never reverted. See
                // canvasHistory.ts; restore is done by undoCanvas/redoCanvas
                // (overlay by scene id), not by zundo's default replace.
                partialize: partializeCanvas,
                equality: canvasEqual,
            }
        )
    );
}

export type EditorStore = ReturnType<typeof createEditorStore>;
